/**
 * Camera and photo library permission handling
 */

import { Camera, CameraPermissionState } from '@capacitor/camera';

export interface PermissionResult {
  granted: boolean;
  state: CameraPermissionState;
  message?: string;
}

/**
 * Check and request photo library permissions
 * Handles all permission states and flows for iOS and Android
 */
export const checkAndRequestPhotoPermissions = async (): Promise<PermissionResult> => {
  try {
    // Check current permission status
    const status = await Camera.checkPermissions();

    // Handle different states
    switch (status.photos) {
      case 'granted':
      case 'limited':
        // Permission granted, can proceed
        return { granted: true, state: status.photos };

      case 'denied':
        // User previously denied, can't request again
        return {
          granted: false,
          state: status.photos,
          message: 'Photo access denied. Please enable in Settings.',
        };

      case 'prompt':
      case 'prompt-with-rationale':
        // Need to request permission
        const result = await Camera.requestPermissions({
          permissions: ['photos'],
        });

        if (result.photos === 'granted' || result.photos === 'limited') {
          return { granted: true, state: result.photos };
        } else {
          return {
            granted: false,
            state: result.photos,
            message: 'Photo access is required to attach images.',
          };
        }

      default:
        return {
          granted: false,
          state: status.photos,
          message: 'Unknown permission state.',
        };
    }
  } catch (error) {
    console.error('Error checking permissions:', error);
    return {
      granted: false,
      state: 'denied',
      message: 'Failed to check permissions. Please try again.',
    };
  }
};

/**
 * Check if we should prompt user to open Settings
 * Returns true if permission is permanently denied (iOS)
 */
export const shouldOpenSettings = (state: CameraPermissionState): boolean => {
  // On iOS, if permission is denied, user must go to Settings
  return state === 'denied';
};

/**
 * Get user-friendly message for permission state
 */
export const getPermissionMessage = (state: CameraPermissionState): string => {
  switch (state) {
    case 'granted':
      return 'Photo access granted';
    case 'limited':
      return 'Limited photo access granted';
    case 'denied':
      return 'Photo access denied. Please enable in Settings.';
    case 'prompt':
    case 'prompt-with-rationale':
      return 'Photo access required';
    default:
      return 'Unknown permission state';
  }
};
